import { CheckCircle2, X, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';

import { PINTEREST_BUSINESS_ACCOUNT_URL, type CallbackError } from '../schemas/channel-state';

export interface CallbackNoticeProps {
  connected: 'pinterest' | undefined;
  error: CallbackError | undefined;
  onDismiss: () => void;
}

/** What the OAuth callback landed with; dismissing clears the search params. */
export function CallbackNotice({ connected, error, onDismiss }: CallbackNoticeProps) {
  const { t } = useTranslation('social');
  if (!connected && !error) return null;

  const dismiss = (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      className="absolute top-2 right-2"
      aria-label={t('notice.dismiss')}
      onClick={onDismiss}
    >
      <X aria-hidden />
    </Button>
  );

  if (error) {
    return (
      <Alert variant="destructive" role="alert" className="relative mb-4 pr-10">
        <XCircle aria-hidden />
        <AlertTitle>{t('notice.errorTitle')}</AlertTitle>
        <AlertDescription>
          {t(`notice.error.${error}`)}
          {error === 'business_required' ? (
            <a
              href={PINTEREST_BUSINESS_ACCOUNT_URL}
              target="_blank"
              rel="noreferrer"
              className="font-medium underline underline-offset-4"
            >
              {t('notice.businessLink')}
            </a>
          ) : null}
        </AlertDescription>
        {dismiss}
      </Alert>
    );
  }

  return (
    <Alert role="status" className="relative mb-4 pr-10">
      <CheckCircle2 aria-hidden className="text-success" />
      <AlertTitle>{t('notice.connected')}</AlertTitle>
      <AlertDescription>{t('notice.connectedHint')}</AlertDescription>
      {dismiss}
    </Alert>
  );
}
